import React, { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col, Collapse, Button } from 'antd';
import {
  IChallengeQuizDermo,
  IChallengeResourceRender,
  IChallengeVideo,
} from 'src/shared/models';
import {
  IResourceDetailList,
  IResourceList,
} from 'src/components/challenge/ChallengeInterfaces';
import {
  ResourceTypeDetailEnum,
  ResourceTypeEnum,
  ChallengeTypeEnum,
  UserResourceEnum,
} from 'src/shared/enums';
import classNames from 'classnames';
import ChallengeVideo from 'src/components/challenge/ChallengeVideo';
import FormationResourceWrapper from 'src/components/challenge/formation/FormationResourceWrapper';
import QuizDermoQuestionsAndAnswers from 'src/components/challenge/formation/QuizDermoQuestionsAndAnswers';
import CustomModal from 'src/components/CustomModal';
import { IRootReducers } from 'src/reducers';
import { customFormatMessage, breakpoints, useWindowSize } from 'utils';
import * as api from 'services/api';
import apiPaths from 'services/apiPaths';
import { CheckCircleOutlined } from '@ant-design/icons';

interface OwnProps extends IChallengeResourceRender {
  //config: IBeautyGuideResource;
  config: Partial<IChallengeQuizDermo>;
  resource: IResourceList;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const { Panel } = Collapse;

const { QUIZ_DERMO_PARTICIPATION } = ResourceTypeEnum;
const { DONE } = UserResourceEnum;

const { QUIZ_DERMO_TITLE, QUIZ_DERMO_DESCRIPTION, QUIZ_DERMO_VIDEO } =
  ResourceTypeDetailEnum;

const FormationQuizDermo: FC<Props> = ({
  config,
  resource,
  updateChallenge,
  updateUserPoints,
}): JSX.Element => {
  const [quizConfig, setQuizConfig] =
    useState<Partial<IChallengeQuizDermo>>(config);
  const [moduleTitle, setModuleTitle] = useState('');
  const [quizTitle, setQuizTitle] = useState('');
  const [quizDescription, setQuizDescription] = useState('');
  const [quizVideo, setQuizVideo] = useState('');
  const [savedAnswers, setSavedAnswers] = useState<number[]>([]);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idResourceParticipation, setIdResourceParticipation] =
    useState<number>(null);

  // ? ResourceDetailList -> QUIZ_DERMO_DESCRIPTION, QUIZ_DERMO_VIDEO
  // ? ResourceList -------> QUESTION
  const { resourceDetailList, resourceList } = resource;
  const { idResource, points, title, questions, isLastQuiz } = config;
  const { formatMessage } = useIntl();

  const { width } = useWindowSize();
  const isMobile = width < breakpoints.lg;

  useEffect(() => {
    if (resource) {
      setModuleTitle(resource.name);

      resourceDetailList.forEach((resourceDetail: IResourceDetailList) => {
        switch (Number(resourceDetail.idResourceTypeD.idResourceTypeD)) {
          case QUIZ_DERMO_TITLE:
            setQuizTitle(resourceDetail.value);
            break;
          case QUIZ_DERMO_DESCRIPTION:
            setQuizDescription(resourceDetail.value);
            break;
          case QUIZ_DERMO_VIDEO:
            setQuizVideo(resourceDetail.value);
            break;
        }
      });

      setIdResourceParticipation(
        resourceList.find((resource: IResourceList) => {
          return (
            resource.idResourceType.idResourceType === QUIZ_DERMO_PARTICIPATION
          );
        }).idResource,
      );
    }
  }, []);

  // ! --------------------------------------------------------------------------  Handlers

  const handleSavedAnswers = (idResourceAnswer: number) => {
    setSavedAnswers([...savedAnswers, idResourceAnswer]);
  };

  const handleSubmitQuiz = async (
    idResourceAnswer: number,
    participationStatus: number,
  ) => {
    try {
      if (participationStatus === DONE) {
        setModalVisible(true);
        setLoading(true);
      }

      const response = await api.putDataCallById({
        dataPath: apiPaths.CHALLENGES.COMPLETE_FORMATION_CON,
        id: idResourceParticipation,
        data: {
          resourceList: [/*...savedAnswers,*/ idResourceAnswer],
          participationStatus,
        },
      });

      const { score, challenge, formationScore } = response.data;

      if (score && typeof score === 'number') updateUserPoints(score);
      if (formationScore && typeof formationScore === 'number')
        setPointsEarned(Number(formationScore));

      updateChallenge(challenge);
    } catch (error) {
      setError(error);
      console.error(error);
    } finally {
      setSavedAnswers([]);
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    //handleShowQuiz(false);
    setSavedAnswers([]);
    setError(null);
    setPointsEarned(0);
    // TODO: esto indica que el módulo se ha completado (AKA 'isQuizDone')
    //setModalQuizDone(true);
  };

  // ! --------------------------------------------------------------------------  Modals Content

  // * --------------------------------------------------------  Module Completed
  const moduleCompletedModalContent = (
    <Row justify="center" align="middle">
      <Col span={20}>
        <Row justify="center" align="middle">
          <div className="icon icon--process-check quiz-dermo-finalize-modal--icon" />
        </Row>
        <Row justify="center">
          <div className="quiz-dermo-finalize-modal--title">
            {formatMessage(
              {
                id: 'formation.quiz-dermo.modal.module-completed.title',
              },
              { br: <br /> },
            )}
          </div>
        </Row>
        <Row
          className="block-small regular-body quiz-dermo-finalize-modal--description"
          justify="center">
          <div>
            {customFormatMessage(
              'formation.quiz-dermo.modal.module-completed.message-{name}-{points}',
              {
                name: moduleTitle,
                points: pointsEarned.toString(),
              },
            )}
          </div>
        </Row>
        <Row justify="center">
          <Button
            // TODO: añadir la funcinonalidad de desbloquear el siguiente módulo
            onClick={handleCloseModal}
            //onClick={handleFinishQuiz}
            className="btn-primary--black quiz-dermo-finalize-modal--button"
            loading={loading}
            block={isMobile}>
            {formatMessage({
              id: isLastQuiz
                ? 'formation.quiz-dermo.modal.module-completed.button.last-module'
                : 'formation.quiz-dermo.modal.module-completed.button.next-module',
            })}
          </Button>
        </Row>
      </Col>
    </Row>
  );

  // * --------------------------------------------------------  Certificate Achieved
  const certificateAchievedModalContent = (
    <Row justify="center" align="middle">
      <Col span={20}>
        <Row justify="center" align="middle">
          <div className="icon icon--process-check quiz-dermo-finalize-modal--icon" />
        </Row>
        <Row justify="center">
          <div className="formation.quiz-dermo.modal.certificate-achieved.title">
            {formatMessage(
              {
                id: 'formation.quiz-dermo.modal.module-completed.title',
              },
              { br: <br /> },
            )}
          </div>
        </Row>
        <Row
          className="block-small regular-body quiz-dermo-finalize-modal--description"
          justify="center">
          <div>
            {customFormatMessage(
              'formation.quiz-dermo.modal.certificate-achieved.message',
              {
                name: moduleTitle,
                points: pointsEarned.toString(),
              },
            )}
          </div>
        </Row>
        {/* 
      <Row>
        <Button
          // TODO: añadir la funcinonalidad de ir al test final
          onClick={null}
          //onClick={handleFinishQuiz}
          className="btn-primary--black quiz-dermo-finalize-modal--button"
          loading={loading}
          block={isMobile}>
          {formatMessage({
            id: 'formation.quiz-dermo.modal.certificate-achieved.button.go-test',
          })}
        </Button>
      </Row>      
      */}
      </Col>
    </Row>
  );

  // ! --------------------------------------------------------------------------  Render

  // ? Render video within Quiz module
  const renderVideo = (videoURL: string) => {
    const configVideo: Partial<IChallengeVideo> = {
      url: videoURL,
      //imgDesktop: null,
      //imgMobile: null,
      image: '',
      title: '',
      // * NORMAL: 0, AUTOPLAY: 1, LIGHT: 2
      //type: 2,
      description: '',
      //points: 0,
      icon: 'campaign-video',
      isImage: false,
      isVideoFile: false,
      isCompleted: config.isCompleted,
      idResource: config.idResource,
    };

    return (
      <ChallengeVideo
        {...{
          challengeType: ChallengeTypeEnum.FORMATION_CON,
          config: configVideo,
          blocked: config.isBlocked,
          updateUserPoints,
          updateChallenge,
          userPoints: 0,
        }}
      />
    );
  };

  const renderQuestionsAndAnswers = () => {
    if (!questions.length) return <></>;

    return (
      <QuizDermoQuestionsAndAnswers
        questions={questions}
        totalResponses={config.totalResponses}
        //showStatistics={showStatistics}
        saveAnswer={handleSavedAnswers}
        handleSubmitQuizDermo={handleSubmitQuiz}
        //allowContinue={allowContinue}
        allowContinue={true}
      />
    );
  };

  // ? Render Quiz module
  const renderModule = () => {
    return (
      <>
        <FormationResourceWrapper
          styleClass="formationDetail--Quiz"
          // isBlocked={config.isBlocked}
        >
          <div className="formationQuizDermo__container">
            <Row
              justify="center"
              align="middle"
              //className="formationQuizDermo__points-container"
            >
              <Col span={24}>
                <Collapse
                  className="formationQuizDermo__collapse "
                  ghost
                  expandIconPosition="right"
                  expandIcon={({ isActive }) => (
                    <div
                      className={classNames('icon icon--arrow-dropdown', {
                        'icon--arrow-dropdown-up': isActive,
                        'icon--arrow-dropdown-down': !isActive,
                      })}
                    />
                  )}>
                  <Panel
                    //className="formationQuizDermo-module__accordion__panel"
                    header={
                      <Row className="formationQuizDermo__collapse-header">
                        {moduleTitle}

                        {config.isCompleted && (
                          <CheckCircleOutlined
                            style={{
                              fontSize: '16px',
                              color: '#027C41',
                              marginRight: '0.5em',
                            }}
                          />
                        )}
                      </Row>
                    }
                    key={config.idResource.toString()}>
                    <div
                      style={{
                        width: '100%',
                        alignItems: 'center',
                        marginBottom: '2rem',
                      }}>
                      <Row className="formationQuizDermo__content-title">
                        {quizTitle}
                      </Row>
                      <Row className="formationQuizDermo__content-description">
                        {/*
                        {quizDescription}
                    */}

                        <span
                          className="steps-section__description"
                          dangerouslySetInnerHTML={{
                            __html: quizDescription,
                          }}
                        />
                      </Row>
                      {quizVideo !== '' && (
                        <Row className="formationQuizDermo__content-video">
                          {renderVideo(quizVideo)}
                        </Row>
                      )}
                      {config.isCompleted && (
                        <Row className="formationQuizDermo__content-label-module-completed">
                          {formatMessage({
                            id: 'formation.quiz-dermo.label.module-completed',
                          })}
                        </Row>
                      )}
                      {config.isPartialDone && (
                        <Row className="formationQuizDermo__content-label-module-started">
                          {formatMessage({
                            id: 'formation.quiz-dermo.label.module-started',
                          })}
                        </Row>
                      )}
                      {!config.isBlocked && (
                        <Row className="formationQuizDermo__content-quiz-container">
                          {renderQuestionsAndAnswers()}
                        </Row>
                      )}
                    </div>
                  </Panel>
                </Collapse>
              </Col>
            </Row>
          </div>
        </FormationResourceWrapper>
        {modalVisible && (
          <CustomModal
            {...{
              visible: modalVisible,
              loading,
              error,
              content: config.isCompleted
                ? moduleCompletedModalContent
                : certificateAchievedModalContent,
              onCancel: handleCloseModal,
              footer: null,
              showIcon: false,
              // TODO: revisar estilo
              customClass: 'quiz-typeform-finalize-modal',
            }}
          />
        )}
      </>
    );
  };

  return <>{renderModule()}</>;
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    challengeType: state.app.challenge?.challengeType,
  };
};

export default FormationQuizDermo;
